import {
  SortAscendingOutlined,
  SortDescendingOutlined
} from "@ant-design/icons"
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tooltip,
  Typography
} from "antd"
import React, { useMemo } from "react"

export interface OrderingDropdownOption<T> {
  key: keyof T
  icon?: React.ReactNode
  label: string
}

type SortOrder = "asc" | "desc" | null

function parseSortValue(value: string | null): {
  key: string | null
  order: SortOrder
} {
  if (!value) return { key: null, order: null }
  if (value.startsWith("-")) {
    return { key: value.slice(1), order: "desc" }
  }
  return { key: value, order: "asc" }
}

function buildSortValue(key: string, order: SortOrder): string | null {
  if (!order) return null
  return order === "desc" ? `-${key}` : key
}

function getNextSortOrder(currentOrder: SortOrder): SortOrder {
  // Цикл: null → asc → desc → null
  if (currentOrder === null) return "asc"
  if (currentOrder === "asc") return "desc"
  return null
}

interface OrderingDropdownProps<T> {
  ordering: string | null
  sortOptions: OrderingDropdownOption<T>[]
  onOrderingChange: (data: string | null) => void
}

export function OrderingDropdown<T>({
  ordering,
  sortOptions,
  onOrderingChange
}: OrderingDropdownProps<T>) {
  const currentSort = useMemo(
    () => parseSortValue(ordering ?? null),
    [ordering]
  )

  const onSortClick = (key: T) => {
    const isSameKey = currentSort.key === key
    const nextOrder = isSameKey ? getNextSortOrder(currentSort.order) : "asc"
    const newValue = buildSortValue(key as string, nextOrder)
    onOrderingChange(newValue)
  }

  const activeSortLabel = useMemo(() => {
    if (!currentSort.key) return null
    const option = sortOptions.find((o) => o.key === currentSort.key)
    return option?.label
  }, [currentSort.key, sortOptions])

  const sortMenuItems: MenuProps["items"] = useMemo(() => {
    return sortOptions.map((option) => {
      const isActive = currentSort.key === option.key
      const order = isActive ? currentSort.order : null

      return {
        ...option,
        key: option.key as string,
        label: (
          <Space>
            <Flex justify="space-between" align="center" gap={12}>
              <Typography.Text strong={isActive}>
                {option.label}
              </Typography.Text>
              {isActive && order && (
                <Typography.Text strong className={"text-base"}>
                  {order === "asc" ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )}
                </Typography.Text>
              )}
            </Flex>
          </Space>
        ),
        onClick: () => onSortClick(option.key as T)
      }
    })
  }, [sortOptions, currentSort])

  return (
    <Dropdown
      menu={{ items: sortMenuItems }}
      trigger={["click"]}
      placement="bottomRight"
    >
      <Tooltip
        placement={"left"}
        title={activeSortLabel ? `Sort by ${activeSortLabel}` : ""}
      >
        <Button
          type={activeSortLabel ? "primary" : "default"}
          icon={
            currentSort.order === "desc" ? (
              <SortDescendingOutlined />
            ) : (
              <SortAscendingOutlined />
            )
          }
        >
          Sort
        </Button>
      </Tooltip>
    </Dropdown>
  )
}
